// helpers for working with Formium components

import sortBy from 'lodash/sortBy';
import {
    FormatDateHelper,
    FormatTimeFromIso,
    toNumberOrNull,
    FormatCurrencyHelper,
    DurationSecondsToHumanized,
 } from './filters-helpers';
import parsePhoneNumber from 'libphonenumber-js/min';

var emptyItem = {
    label: '',
    help_text: '',
    choices: '',
    type: '',
    required: false,
    id: null
};

const itemTypes = [
    /* FORMAT:
    {
        id: 1,  // used for ordering in list, only on front end (also used as key in vue v-for it seems, but that is kinda dumb)
        value: 'text-input',  // must match "slug" used in django, lookups on front-end are based on this
        text: 'Short Text Response',   // text used on front end for display
        typeClass: 'formbuilder.formiumitems.FormiumTextInputType',   // before you send back to API, you map value slug to this, and send this back
        hasChoices: false,   // used to show/hide choices field and to conditionally validate choices are there
        defaultLabel: '',   // default question text
        iconName: 'textarea-t',   // icon name, based on Bootstrap icons
        showInList: true    // boolean that shows/hides this field in list of fields to be added (allows you to disable fields for future use, but still keep for legacy)
        valueStringFormatter: a function that takes 1 arg (value) and returns a formatted string (handles dates, durations, etc.) - use in valueFormatter in ag-grid, etc.
    }
    */
    {
        id: 1,
        value: 'text-input',
        text: 'Short Text Response',
        typeClass: 'formbuilder.formiumitems.FormiumTextInputType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'textarea-t',
        showInList: true,
        valueStringFormatter: (value) => value,
    },{
        id: 2,
        value: 'text-area',
        text: 'Longer Text Response',
        typeClass: 'formbuilder.formiumitems.FormiumTextAreaType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'justify-left',
        showInList: true,
        valueStringFormatter: (value) => value,
    },{
        id: 7,
        value: 'email',
        text: 'Email Address',
        typeClass: 'formbuilder.formiumitems.FormiumEmailFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'at',
        showInList: true,
        valueStringFormatter: (value) => value,
    },{
        id: 9,
        value: 'date',
        text: 'Date',
        typeClass: 'formbuilder.formiumitems.FormiumDateFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'calendar',
        showInList: true,
        valueStringFormatter: (value) => FormatDateHelper(value),
    },{
        id: 10,
        value: 'time',
        text: 'Time',
        typeClass: 'formbuilder.formiumitems.FormiumTimeFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'clock',
        showInList: true,
        valueStringFormatter: (value) => FormatTimeFromIso(value),
    },{
        id: 3,
        value: 'select-yes-no',
        text: 'Yes / No Question',
        typeClass: 'formbuilder.formiumitems.FormiumSelectYesNoFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'question-circle',
        showInList: true,
        valueStringFormatter: (value) => value,
    },{
        id: 4,
        value: 'checkbox-confirm',
        text: 'Checkbox Confirmation',
        typeClass: 'formbuilder.formiumitems.FormiumCheckboxConfirmFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'check2-square',
        showInList: false,  // NOT READY YET (as of June 2026) -- need to make sure we handle 'required' correctly.... so unchecked means the entire form is invalid????  need backend to honor that...
        valueStringFormatter: (value) => value === true ? 'Yes' : 'No',  // stored as boolean, but we want to show Yes/No in UI?
    },{
        id: 5,
        value: 'single-select',
        text: 'Choices - Pick One',
        typeClass: 'formbuilder.formiumitems.FormiumSingleSelectFieldType',
        hasChoices: true,
        defaultLabel: '',
        iconName: 'check2',
        showInList: true,
        valueStringFormatter: (value) => {  // single selects should just be a single string from backend... but in case we had array...
            if (value && Array.isArray(value)) {
                return value[0];  // if its an array, just return the first element, should only be one
            }
            return value;  // otherwise just return the value
        },
    },{
        id: 6,
        value: 'multiple-select',
        text: 'Choices - Pick Multiple',
        typeClass: 'formbuilder.formiumitems.FormiumMultipleSelectFieldType',
        hasChoices: true,
        defaultLabel: '',
        iconName: 'check2-all',
        showInList: true,
        valueStringFormatter: (value) => {
            if (value && Array.isArray(value)) {
                return value.join(', ');  // return comma-separated list of all array elements
            }
            return value;
        },
    },{
        id: 8,
        value: 'telephone-simple',
        typeClass: 'formbuilder.formiumitems.FormiumTelephoneFieldType',
        text: 'Telephone',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'telephone',
        showInList: true,
        valueStringFormatter: (value) => {
            if (!value) return '';
            const phoneNumber = parsePhoneNumber(value);  // might not be able to parse it, so could be null/undefined?
            if (!phoneNumber || !phoneNumber.isValid()) {
                return value;  // if not valid, just return the original value because it might still mean something
            }  // otherwise, we'll return the international formatted phone number
            return phoneNumber.formatInternational();
        },
    },{
        id: 11,
        value: 'duration',
        text: 'Time Duration',
        typeClass: 'formbuilder.formiumitems.FormiumDurationFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'stopwatch',
        showInList: true,
        valueStringFormatter: (value) => { // backend seems to return durations as a Number of seconds
            return DurationSecondsToHumanized(value);
        },
    },{
        id: 12,
        value: 'decimal',
        text: 'Number',
        typeClass: 'formbuilder.formiumitems.FormiumDecimalFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'hash',
        showInList: true,
        valueStringFormatter: (value) => toNumberOrNull(value),
    },{
        id: 13,
        value: 'django-money-field',
        text: 'Money',
        typeClass: 'formbuilder.formiumitems.FormiumDjMoneyFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'cash-stack',
        showInList: true,
        valueStringFormatter: (value) => {
            const amount = value ? value.amount : null;
            const currCode = value ? value.currency : '';
            return FormatCurrencyHelper(amount, currCode);  // backend returns an OBJECT with {amount (Number), currency (String - 3 digit code)}
        },
    },{
        id: 14,
        value: 'file-upload-direct',
        text: 'File Upload',
        typeClass: 'formbuilder.formiumitems.FormiumFileDirectUploadFieldType',
        hasChoices: false,
        defaultLabel: '',
        iconName: 'paperclip',
        showInList: false,   // hiding this -- move away from s3direct and use your own -- still need to implement it
        valueStringFormatter: (value) => value,  // TBD...
    },{
        id: 15,
        value: 'simple-rating',
        text: 'Rating Scale',
        typeClass: 'formbuilder.formiumitems.FormiumSimpleRatingFieldType',
        hasChoices: true,  // but we will use pre-defined choices [1, 2, 3, 4, 5], etc.
        defaultLabel: '',
        iconName: 'star',
        showInList: true, 
        valueStringFormatter: (value) => value,  // TBD...
    }
];

const simpleRatingChoicesOptions = [  // value should be a comma-separated string of text (numbers as strings) that we will store as item.choices in backend
    {value: '1,2,3', text: '1 - 3'},
    {value: '1,2,3,4,5', text: '1 - 5'},
    {value: '1,2,3,4,5,6,7', text: '1 - 7'},
    {value: '1,2,3,4,5,6,7,8,9,10', text: '1 - 10'},
];

const allItemTypes = sortBy(itemTypes, ['id']);  // all item types, sorted by id (which is the order we want to show them in on the front end)
// const availableItemTypes = allItemTypes.filter(it => it.showInList === true);  // only the item types that should be shown in the list of options when adding a new item to the form (filters out ones we want to keep for legacy but not show as options for new items)
// we can just do the filtering in the component...

// const filteredItemTypes = itemTypes.filter(type => type.showInList === true);  // filtered but not sorted -- don't do that here... do that in the component?
// const rfxItemTypes = sortBy(filteredItemTypes, ['id']);  // now sorted

export {
    emptyItem,
    itemTypes,  // the full list, but not sorted... that's it
    allItemTypes,  // sorted by ID... kinda silly to have both, but legacy
    // availableItemTypes,  // filtered list based on showInList boolean
    // filteredItemTypes,  // don't need to export
    // rfxItemTypes,
    simpleRatingChoicesOptions,
}